import React, { memo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { isEqual, isUndefined } from "lodash";
import themeConfiguration from "config/themeConfiguration";
import Helmet from "react-helmet";
import { pushDataLayerEvent } from "utils/dataLayer";

const SeoHelmet = memo(
  ({ title, url, description, image, card, useAppName }) => {
    if (isUndefined(title) || isUndefined(url)) return <></>;
    let realTitle =
      title !== ""
        ? typeof useAppName !== "undefined" && useAppName === true
          ? title + " - " + themeConfiguration.name_homepage
          : title + " - " + themeConfiguration.name
        : themeConfiguration.name_homepage;
    let finalUrl = themeConfiguration.app_url + url;
    finalUrl = finalUrl.replace(".com//", ".com/");

    const pagePath = window.location.pathname + window.location.search;
    const trackedPath = useRef(null);

    // SPA route changes never reload the page, so the GA4 tag in GTM listens for
    // this event instead of relying on an automatic page view. The ref keeps a
    // re-render with the same path from counting twice.
    useEffect(() => {
      if (trackedPath.current === pagePath) return;
      trackedPath.current = pagePath;

      pushDataLayerEvent("virtual_page_view", {
        page_path: pagePath,
        page_title: realTitle,
        page_location: window.location.href,
      });
    }, [pagePath, realTitle]);

    const metaDefaultTag = document.querySelector('meta[name="description"]');

    useEffect(() => {
      if (description && metaDefaultTag) {
        metaDefaultTag.remove();
      }
    }, [description, metaDefaultTag]);

    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{realTitle}</title>
          {description && <meta name="description" content={description} />}

          {/** facebook */}
          <meta property="og:title" content={realTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={image} />
          <meta property="og:url" content={finalUrl} />

          {/** twitter */}
          <meta name="twitter:title" content={realTitle} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image} />
          <meta
            name="twitter:card"
            content={typeof card === "undefined" ? "summary_large_image" : card}
          />

          <link rel="canonical" href={finalUrl} />
        </Helmet>
      </>
    );
  },
  (prevProps, nextProps) => {
    if (isEqual(prevProps, nextProps)) {
      return true;
    }

    return false;
  }
);

SeoHelmet.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
};

export default SeoHelmet;
